import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from "recompose";
import {changeListParams as changeListParamsAction} from 'admin-on-rest/lib/actions/listActions';
import DefaultActions from 'admin-on-rest/lib/mui/list/Actions';
import queryReducer, {SET_FILTER} from 'admin-on-rest/lib/reducer/admin/resource/list/queryReducer';
import ViewTitle from 'admin-on-rest/lib/mui/layout/ViewTitle';
import {loadFuseData} from '../restClient';
import PageDescription from './PageDescription';
import {commonStyles} from '../shared/styles';
import '../shared/react-table.css';
import {Card, CardText} from 'material-ui/Card';
import CircularProgress from 'material-ui/CircularProgress';
import Title from 'admin-on-rest/lib/mui/layout/Title';

const styles = {
    noResults: {padding: 20},
    header: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    loader: {
        display: 'inline-block',
        margin: '0 6px 0 20px'
    }
};

/**
 * A List component that retrieves data from a requested resource and populates a ReactTable
 *
 */
class ListView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: {},
            ids: [''],
            isLoading: false,
            params: {},
            total: 0
        };
    }

    componentDidMount() {
        if (!this.props.requiresFilter) {
            this.updateData();
        }
    }

    componentWillReceiveProps(nextProps, nextContext) {
        if (
            nextProps.resource !== this.props.resource ||
            nextProps.filterValues !== this.props.filterValues ||
            nextProps.params !== this.props.params
        ) {
            this.setState({
                filterValues: nextProps.filterValues,
                params: nextProps.params
            });
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            JSON.stringify(prevProps.filterValues) !== JSON.stringify(this.props.filterValues) ||
            JSON.stringify(prevProps.params) !== JSON.stringify(this.props.params)
        ) {
            this.updateData();
        }
    }

    getBasePath() {
        return this.props.location.pathname.replace(/\/$/, '');
    }

    changeParams(action) {
        const query = {
            sort: this.props.sort.field,
            order: this.props.sort.order,
            page: 1,
            perPage: this.props.perPage,
            filter: {}
        };
        const newParams = queryReducer(query, action);
        this.props.changeListParams(this.props.resource, newParams);
    }

    setFilters = filters => {
        this.changeParams({type: SET_FILTER, payload: filters})
    };

    hideFilter = filterName => {
        // Unused since most List views disallow query strings
    };


    updateData() {
        const resolveData = (e) => {
            let data = (e.data) ? e.data : [];
            if (data.length > 0 && data[0].id === '') {
                data = [];
            }
            // Convert data array to array of ID objects
            let dataObj = {};
            data.forEach(function(item, i) {
                if (item.id) {
                    dataObj[item.id] = item;
                }
            });
            let ids = (e.ids) ? e.ids : [''];

            this.setState({
                data: dataObj,
                ids: ids,
                isLoading: false,
                total: e.total
            })
        };

        const rejectData = (e) => {
            this.setState({
                data: [{id: '', name: ''}],
                ids: [''],
                isLoading: false,
                total: 0
            })
        };

        this.setState({
            isLoading: true
        }, () => {
            const getData = new Promise((resolve, reject) => {
                loadFuseData('GET_LIST', this.props.resource, this.props.params, resolveData, rejectData);
            });
            getData.then(data => {
            }).catch(error => {
            });
        });
    }

    render() {

        const {
            actions = <DefaultActions/>,
            children,
            desc,
            filters,
            filterValues,
            hasCreate,
            name,
            params,
            resource,
            title
        } = this.props;

        const {
            data,
            ids,
            isLoading,
            total
        } = this.state;

        const basePath = this.getBasePath();

        const titleElement = (
            <Title title={title} defaultTitle={resource}/>
        );
        const descriptionElement = (desc) ? (
            <PageDescription desc={desc}/>
        ) : (
            ''
        );

        return (
            <div className="list-page">
                <Card style={{opacity: isLoading ? 0.8 : 1}}>
                    <div style={styles.header}>
                        <ViewTitle title={titleElement}/>
                        {actions &&
                            React.cloneElement(actions, {
                                resource,
                                basePath,
                                hasCreate,
                                displayedFilters: this.state
                            })}
                    </div>
                    {descriptionElement}

                    {filters &&
                        React.cloneElement(filters, {
                            resource,
                            hideFilter: this.hideFilter,
                            filterValues,
                            displayedFilters: this.state,
                            setFilters: this.setFilters,
                            context: 'form',
                        })
                    }

                    {isLoading &&
                        <div style={commonStyles.loaderContainer}>
                            <h4>Retrieving {name}...</h4>
                            <CircularProgress
                                size={24}
                                thickness={2}
                                className="loader"
                            />
                        </div>
                    }
                    {isLoading || total > 0 ? (
                        <div>
                            {children &&
                                React.cloneElement(children, {
                                    resource,
                                    ids,
                                    data,
                                    currentSort: {
                                        field: params.sort,
                                        order: params.order
                                    },
                                    basePath,
                                    isLoading,
                                    filterValues: filterValues,
                                    setSort: this.setSort,
                                })}
                        </div>
                    ) : (
                        <CardText>
                            No results found.
                        </CardText>
                    )}

                </Card>
            </div>
        )
    }
}

ListView.propTypes = {
    actions: PropTypes.element,
    allowQueryString: PropTypes.bool,
    changeListParams: PropTypes.func.isRequired,
    children: PropTypes.node,
    desc: PropTypes.any,
    filters: PropTypes.element,
    filterValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    hasCreate: PropTypes.bool,
    id: PropTypes.string,
    location: PropTypes.object.isRequired,
    name: PropTypes.string,
    perPage: PropTypes.number.isRequired,
    requiresFilter: PropTypes.bool,
    resource: PropTypes.string.isRequired,
    sort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.string,
    }),
    title: PropTypes.any
};

ListView.defaultProps = {
    allowQueryString: true,
    filter: {},
    filterValues: {},
    filters: null,
    id: 'list',
    name: 'List',
    perPage: 20,
    sort: {
        field: 'id',
        order: 'DESC',
    },
};

function mapStateToProps(state, props) {
    const resourceState = state.admin.resources[props.resource];
    return {
        params: resourceState.list.params,
        total: resourceState.list.total,
        isLoading: state.admin.loading > 0,
        filterValues: resourceState.list.params.filter
    };
}

const enhance = compose(
    connect(mapStateToProps, {
        changeListParams: changeListParamsAction,
    })
);

export default enhance(ListView);



// WEBPACK FOOTER //
// ./src/Fuse/components/ListView.js